import { useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { DateRangePicker } from "@/components/DatePicker";
import { X, Loader2, Search, ArrowRightLeft } from "lucide-react";

const inputCls = "mt-1 w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#FFC817]/50";
const labelCls = "text-[10px] uppercase tracking-wider text-[#A8A29E]";

/**
 * Phase 2 of the Operational Control Upgrade (2026-02): the ONE shared reassignment workflow,
 * used from BOTH entry points (Project's Crew/Assignments section AND a freelancer's own
 * profile page) — same component, same two backend calls (Cancel-with-Reason, then a normal
 * NJM Direct create), same full offer→negotiate→accept→approve lifecycle for the replacement.
 * `assignment` must carry {id, name, project_id, project_name}.
 */
export function ReassignAssignmentModal({ assignment, onClose, onDone }) {
  const [step, setStep] = useState("reason");
  const [reason, setReason] = useState("");
  const [freelancers, setFreelancers] = useState([]);
  const [query, setQuery] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ brief: "", location: "", shoot_dates: { start: null, end: null }, deliverables: "", proposed_fee: "" });
  const [submitting, setSubmitting] = useState(false);

  const goToPickReplacement = () => {
    if (!reason.trim()) { toast.error("A reason is required to remove this freelancer"); return; }
    if (freelancers.length === 0) api.get("/v1/build/freelancers").then((r) => setFreelancers(r.data.items || [])).catch(() => {});
    setStep("pick");
  };

  const filtered = query.trim()
    ? freelancers.filter((f) => `${f.name} ${f.primary_role} ${f.skills || ""}`.toLowerCase().includes(query.toLowerCase()))
    : freelancers;

  const submit = async () => {
    if (!selected) { toast.error("Select a replacement freelancer"); return; }
    setSubmitting(true);
    try {
      await api.post(`/v1/build/assignments/${assignment.id}/reassign`, {
        reason: reason.trim(), freelancer_id: selected.id,
        brief: form.brief || undefined, location: form.location || undefined,
        shoot_dates: (form.shoot_dates.start || form.shoot_dates.end) ? form.shoot_dates : undefined,
        deliverables: form.deliverables || undefined,
        proposed_fee: form.proposed_fee !== "" ? Number(form.proposed_fee) : undefined,
      });
      toast.success("Removed and a fresh offer sent to the replacement freelancer.");
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't submit the reassignment");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="reassign-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="reassign-modal-backdrop" />
      <div className="relative w-full max-w-lg rounded-lg bg-[#FFFFFF] border border-[#E7E5E4] shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-[#E7E5E4] px-5 py-4">
          <h2 className="font-editorial text-lg tracking-tight inline-flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4" /> Reassign Crew
          </h2>
          <button data-testid="reassign-modal-close" onClick={onClose} className="text-[#A8A29E] hover:text-[#1C1C1E]"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-xs text-[#57534E]">
            Removing <span className="font-medium text-[#1C1C1E]">{assignment.name}</span>
            {assignment.project_name && <> from <span className="font-medium text-[#1C1C1E]">{assignment.project_name}</span></>}.
            This does not delete the assignment. It's archived as Cancelled with your reason, and a fresh offer restarts the normal flow for the replacement.
          </div>

          {step === "reason" ? (
            <div>
              <label className={labelCls}>Reason for removal *</label>
              <textarea data-testid="reassign-reason-input" rows={3} className={inputCls}
                value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Freelancer unavailable, scheduling conflict, quality concern…" />
            </div>
          ) : (
            <>
              <div className="relative">
                <label className={labelCls}>Replacement Freelancer</label>
                {selected ? (
                  <div className="mt-1 flex items-center justify-between rounded-md border border-[#FFC817]/40 bg-[#FFC817]/10 px-3 py-2 text-sm">
                    <span data-testid="reassign-selected-freelancer">{selected.name} · {selected.primary_role}</span>
                    <button onClick={() => setSelected(null)} data-testid="reassign-freelancer-clear-btn" className="text-[#B45309] hover:text-[#1C1C1E]"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ) : (
                  <>
                    <div className="relative mt-1">
                      <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-[#A8A29E]" />
                      <input
                        value={query} onChange={(e) => { setQuery(e.target.value); setPickerOpen(true); }} onFocus={() => setPickerOpen(true)}
                        placeholder="Search freelancers by name, role or skill…" data-testid="reassign-freelancer-search-input"
                        className={`${inputCls} pl-8 mt-0`}
                      />
                    </div>
                    {pickerOpen && (
                      <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto rounded-md border border-[#E7E5E4] bg-[#FFFFFF] shadow-lg">
                        {filtered.length === 0 ? (
                          <div className="px-3 py-3 text-xs text-[#A8A29E]">No freelancers found</div>
                        ) : filtered.slice(0, 30).map((f) => (
                          <button key={f.id} type="button" data-testid={`reassign-freelancer-option-${f.id}`}
                            onClick={() => { setSelected(f); setPickerOpen(false); }}
                            className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-[#F5F5F4]">
                            <span className="truncate">{f.name}</span>
                            <span className="shrink-0 text-[10px] text-[#A8A29E]">{f.primary_role}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className={labelCls}>Brief</label>
                  <textarea data-testid="reassign-form-brief" rows={2} className={inputCls} value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Location</label>
                  <input data-testid="reassign-form-location" className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Shoot Dates</label>
                  <DateRangePicker value={form.shoot_dates} onChange={(v) => setForm({ ...form, shoot_dates: v })} testId="reassign-form-shoot-dates" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Deliverables</label>
                  <textarea data-testid="reassign-form-deliverables" rows={2} className={inputCls} value={form.deliverables} onChange={(e) => setForm({ ...form, deliverables: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Proposed Fee</label>
                  <input data-testid="reassign-form-proposed-fee" type="number" className={inputCls} value={form.proposed_fee} onChange={(e) => setForm({ ...form, proposed_fee: e.target.value })} />
                </div>
              </div>
            </>
          )}
        </div>

        <div className="border-t border-[#E7E5E4] px-5 py-4 flex justify-between gap-2">
          {step === "pick" ? (
            <button onClick={() => setStep("reason")} data-testid="reassign-back-btn" className="rounded-md border border-[#E7E5E4] px-3 py-1.5 text-xs text-[#57534E] hover:bg-[#F5F5F4]">Back</button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} data-testid="reassign-cancel-btn" className="rounded-md border border-[#E7E5E4] px-3 py-1.5 text-xs text-[#57534E] hover:bg-[#F5F5F4]">Cancel</button>
            {step === "reason" ? (
              <button onClick={goToPickReplacement} data-testid="reassign-continue-btn"
                className="inline-flex items-center gap-1.5 rounded-md bg-[#FFC817] px-3.5 py-1.5 text-xs font-medium text-[#1C1C1E] hover:bg-[#E6B415]">
                Continue: Select Replacement
              </button>
            ) : (
              <button onClick={submit} disabled={submitting} data-testid="reassign-submit-btn"
                className="inline-flex items-center gap-1.5 rounded-md bg-[#FFC817] px-3.5 py-1.5 text-xs font-medium text-[#1C1C1E] hover:bg-[#E6B415] disabled:opacity-50">
                {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Submit for Approval
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
